import 'reflect-metadata';
import { map, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { SecureHttp } from '../../auth/securehttp';
import { Observable } from 'rxjs';
import { PagedResults } from '../../models/paged-results.model';
import { MemberUpdate } from '../../models/member-update.model';
import { Change } from '../../models/change.model';
import { StatusResponse } from '../../models/status-response.model';
import { SelectOption } from '../../models/select-option.model';
import { HttpParams } from '@angular/common/http';
import { Member, MemberEdit, DuplicateMember } from 'app/models/member.model';
import { FileService } from '../../common/services/file.service';

@Injectable()
export class MembersService {

    constructor(
        private http: SecureHttp,
        private fileService: FileService
    ) { }

    query(options?: any): Observable<Member[]> {
        let url = 'api/members';
        let params: HttpParams = new HttpParams();

        if (options) {
            for (let key in options) {
                if (key && options[key]) {
                    params = params.set(key, options[key]);
                }
            }
        }

        return this.http.get(url, { params: params }).pipe(
            map(response => response as Member[]));
    }

    refresh(accountNum: string): Observable<Member[]> {
        let url = 'api/members/refresh';
        let params = new HttpParams()
        params = params.set('account', accountNum)
        return this.http.get(url, { params: params }).pipe(
            map(response => response as Member[]));
    }

    paged(options?: any): Observable<PagedResults<Member>> {

        let url = 'api/members/paged';
        let params: HttpParams = new HttpParams();

        if (options) {
            for (let key in options) {
                if (key && options[key]) {
                    params = params.set(key, options[key]);
                }
            }
        }

        return this.http.get(url, { params: params }).pipe(
            map(response => response as PagedResults<Member>));
    }

    get(id: string): Observable<Member> {

        let url = `api/members/${id}`;
        return this.http.get(url).pipe(
            map(response => response as Member));
    }

    getUpdates(options?: any): Observable<MemberUpdate[]> {

        let url = 'api/members/updates';
        let params: HttpParams = new HttpParams();

        if (options) {
            for (let key in options) {
                if (key && options[key]) {
                    params = params.set(key, options[key]);
                }
            }
        }

        return this.http.get(url, { params: params }).pipe(
            map(response => response as MemberUpdate[]));
    }

    getUpdatesPaged(options?: any): Observable<PagedResults<MemberUpdate>> {

        let url = 'api/members/updates/paged';
        let params: HttpParams = new HttpParams();

        if (options) {
            for (let key in options) {
                if (key && options[key]) {
                    params = params.set(key, options[key]);
                }
            }
        }

        return this.http.get(url, { params: params }).pipe(
            map(response => response as PagedResults<MemberUpdate>));
    }

    getUpdate(id: string): Observable<MemberUpdate> {

        let url = 'api/members/update';
        let params: HttpParams = new HttpParams();
        params = params.set('id', id);

        return this.http.get(url, { params: params }).pipe(
            map(response => response as MemberUpdate));
    }

    getUpdateDetails(id: string): Observable<Change[]> {

        let url = 'api/members/update/details';
        let params: HttpParams = new HttpParams();
        params = params.set('id', id);

        return this.http.get(url, { params: params }).pipe(
            map(response => response as Change[]));
    }

    cancelUpdate(id: string): Observable<StatusResponse> {

        let url = `api/members/update`;

        let params: HttpParams = new HttpParams();
        params = params.set('id', id);

        return this.http.delete(url, { params: params }).pipe(map(response => response as StatusResponse));
    }

    create(member: MemberEdit): Observable<StatusResponse> {

        let url = 'api/members';
        return this.http.post(url, member).pipe(map(response => response as StatusResponse));
    }

    update(member: MemberEdit): Observable<StatusResponse> {

        let url = `api/members`;
        return this.http.put(url, member).pipe(map(response => response as StatusResponse));
    }

    updateSalaries(members: { Members: Member[] }): Observable<StatusResponse> {

        let url = `api/members/salary`;
        return this.http.put(url, members).pipe(map(response => response as StatusResponse));
    }

    terminate(member: MemberEdit): Observable<StatusResponse> {

        let url = `api/members/terminate`;
        return this.http.put(url, member).pipe(map(response => response as StatusResponse));
    }

    checkDuplicate(options?: any): Observable<DuplicateMember> {
        let url = `api/members/duplicate`;
        let params: HttpParams = new HttpParams();

        if (options) {
            for (let key in options) {
                if (key && options[key]) {
                    params = params.set(key, options[key]);
                }
            }
        }

        return this.http.get(url, { params: params }).pipe(
            map(response => response as DuplicateMember));
    }

    typeahead(filter: string, status?: string): Observable<SelectOption[]> {
        let url = `api/members/typeahead`;
        let params: HttpParams = new HttpParams();
        params = params.set('filter', filter);
        if (status) {
            params = params.set('status', status);
        }
        return this.http.get(url, { params: params }).pipe(
            map(response => response as SelectOption[]));
    }

    census(salary: boolean, type: string, format: string): Observable<any> {
        let url = `api/documents/census/download`;
        let params = new HttpParams();
        params = params.set('salary', salary.toString());
        params = params.set('type', type);
        params = params.set('format', format);
        return this.http.get(url, { params: params }).pipe(tap((response) => {
            this.fileService.downloadDocument(response);
        }));
    }

    download(ids: string[]): Observable<any> {
        let url = `api/members/download`;
        let params = new HttpParams();
        params = params.set('ids', ids.join(','));
        return this.http.get(url, { params: params }).pipe(tap((response) => {
            this.fileService.downloadDocument(response);
        }));

    }

    upload(file: File): Observable<StatusResponse> {
        const formData = new FormData();
        formData.append('file', file, file.name);

        return this.http.post('api/members/upload', formData).pipe(
            map(response => response as StatusResponse));
    }
}
